/**
 * Pure evaluation of the canonical filter AST against plain row records.
 *
 * Consumes the resolved filter tree produced by {@link resolveFilterSourceRefs}
 * — every value is already projected into a typed shape (`{ string }`,
 * `{ number }`, `{ date }`, `{ ref }`, `{ array }`, `{ refs }`) that mirrors
 * the LLM-facing literal schemas. The evaluator dispatches on op family,
 * then on field presence — no `??` fallbacks.
 *
 * Separated from the Arquero engine so the evaluator can be reused by any
 * pipeline step that operates on {@link Dataset} rows, and so filter
 * semantics are unit-testable in isolation.
 */
import type { Dayjs } from 'dayjs';

import { tryParseDate } from '#core/lib/utils/date.ts';
import { getNestedValue } from '#core/lib/utils/object.ts';

import {
  OP_FAMILY,
  type OpFamily,
  type ResolvedArray,
  type ResolvedLeaf,
  type ResolvedRootFilter,
  type ResolvedScalar,
  refEquals,
} from './resolve-source-refs.ts';

/** Lowercase a string when caseSensitive is false; pass through otherwise. */
function normalize(value: unknown, caseSensitive: boolean): unknown {
  if (typeof value === 'string' && !caseSensitive) {
    return value.toLowerCase();
  }
  return value;
}

/**
 * Evaluates a single leaf filter against a row.
 *
 * Column references support dot-notation for nested object fields
 * (e.g. `string.externalId` → `row.string.externalId`), matching the
 * dot-notation semantics of `applySelect` in the engine. Resolved via
 * `getNestedValue` so both code paths share one source of truth.
 */
export function evaluateLeafFilter(row: Record<string, unknown>, leaf: ResolvedLeaf): boolean {
  const cell = getNestedValue(row, leaf.column);
  return FAMILY_PREDICATES[OP_FAMILY[leaf.op]](cell, leaf);
}

// Family-keyed dispatch. The resolver guarantees each leaf carries the
// right value/literal shape for its op family — no fallback needed.
type FamilyPredicate = (cell: unknown, leaf: ResolvedLeaf) => boolean;

const FAMILY_PREDICATES: Record<OpFamily, FamilyPredicate> = {
  null: (cell, leaf) => (leaf.op === 'isNull' ? isNullish(cell) : !isNullish(cell)),

  pattern: (cell, leaf) => {
    const l = leaf as Extract<ResolvedLeaf, { op: 'contains' | 'startsWith' | 'endsWith' | 'regex' }>;
    return stringPattern(l.op, cell, l.literal.string, l.caseSensitive ?? false);
  },

  equals: (cell, leaf) => {
    const l = leaf as Extract<ResolvedLeaf, { op: 'equals' | 'notEquals' }>;
    const matches = scalarEquals(cell, l.value, l.caseSensitive ?? false);
    return l.op === 'equals' ? matches : !matches;
  },

  comparison: (cell, leaf) => {
    const l = leaf as Extract<ResolvedLeaf, { op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' }>;
    return scalarCompare(l.op, cell, l.value);
  },

  set: (cell, leaf) => {
    const l = leaf as Extract<ResolvedLeaf, { op: 'in' | 'notIn' }>;
    const member = setMember(cell, l.value, l.caseSensitive ?? false);
    return l.op === 'in' ? member : !member;
  },
};

const isNullish = (cell: unknown): boolean => cell === null || cell === undefined;

// String patterns — contains/startsWith/endsWith/regex.

type StringPatternOp = 'contains' | 'startsWith' | 'endsWith' | 'regex';

const STRING_PATTERN_MATCHERS: Record<Exclude<StringPatternOp, 'regex'>, (cell: string, pattern: string) => boolean> = {
  contains: (cell, pattern) => cell.includes(pattern),
  startsWith: (cell, pattern) => cell.startsWith(pattern),
  endsWith: (cell, pattern) => cell.endsWith(pattern),
};

function stringPattern(op: StringPatternOp, cell: unknown, pattern: string, caseSensitive: boolean): boolean {
  if (typeof cell !== 'string') {
    return false;
  }
  if (op === 'regex') {
    try {
      return new RegExp(pattern, caseSensitive ? '' : 'i').test(cell);
    } catch {
      return false;
    }
  }
  const c = normalize(cell, caseSensitive) as string;
  const p = normalize(pattern, caseSensitive) as string;
  return STRING_PATTERN_MATCHERS[op](c, p);
}

// Scalar equality (equals/notEquals). Dispatches by field presence — the
// resolved value's shape mirrors the LLM-facing literal schemas.

function scalarEquals(cell: unknown, value: ResolvedScalar, caseSensitive: boolean): boolean {
  if ('ref' in value) {
    return refEquals(cell, value.ref);
  }
  if ('string' in value) {
    return normalize(cell, caseSensitive) === normalize(value.string, caseSensitive);
  }
  if ('number' in value) {
    return cell === value.number;
  }
  // 'date'
  const cellDate = tryParseDate(cell);
  const targetDate = tryParseDate(value.date);
  return cellDate !== null && targetDate !== null && cellDate.isSame(targetDate);
}

// Scalar comparison (eq/neq/gt/gte/lt/lte). Lookup-driven — one row per op.

type CompareOp = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte';

const NUMBER_COMPARATORS: Record<CompareOp, (a: number, b: number) => boolean> = {
  eq: (a, b) => a === b,
  neq: (a, b) => a !== b,
  gt: (a, b) => a > b,
  gte: (a, b) => a >= b,
  lt: (a, b) => a < b,
  lte: (a, b) => a <= b,
};

const DATE_COMPARATORS: Record<CompareOp, (a: Dayjs, b: Dayjs) => boolean> = {
  eq: (a, b) => a.isSame(b),
  neq: (a, b) => !a.isSame(b),
  gt: (a, b) => a.isAfter(b),
  gte: (a, b) => a.isAfter(b) || a.isSame(b),
  lt: (a, b) => a.isBefore(b),
  lte: (a, b) => a.isBefore(b) || a.isSame(b),
};

function scalarCompare(op: CompareOp, cell: unknown, value: ResolvedScalar): boolean {
  if ('date' in value) {
    const cellDate = tryParseDate(cell);
    const targetDate = tryParseDate(value.date);
    if (cellDate === null || targetDate === null) {
      return op === 'neq';
    }
    return DATE_COMPARATORS[op](cellDate, targetDate);
  }
  if ('number' in value) {
    if (typeof cell !== 'number') {
      return op === 'neq' ? cell !== value.number : op === 'eq' ? cell === value.number : false;
    }
    return NUMBER_COMPARATORS[op](cell, value.number);
  }
  // 'string' / 'ref' are not legal payloads for numeric/date comparison ops;
  // the resolver does not produce them. Defensive fall-through.
  return false;
}

// Set membership (in/notIn).

function setMember(cell: unknown, value: ResolvedArray, caseSensitive: boolean): boolean {
  if ('refs' in value) {
    return value.refs.some((ref) => refEquals(cell, ref));
  }
  const normalizedCell = normalize(cell, caseSensitive);
  return value.array.some((item) => normalize(item, caseSensitive) === normalizedCell);
}

/** Type guard distinguishing a resolved leaf from a resolved level-1 group. */
export function isLeafFilter(node: unknown): node is ResolvedLeaf {
  return typeof node === 'object' && node !== null && 'op' in node && !('type' in node);
}

/** Evaluates a resolved filter tree (root, groups, and leaves) against a row. */
export function evaluateFilterTree(row: Record<string, unknown>, filter: ResolvedRootFilter): boolean {
  const results = filter.filters.map((node) =>
    isLeafFilter(node) ? evaluateLeafFilter(row, node) : evaluateFilterTree(row, node as ResolvedRootFilter),
  );

  if (filter.type === 'and') {
    return results.every(Boolean);
  }
  if (filter.type === 'or') {
    return results.some(Boolean);
  }
  // 'not': negate the AND of all results
  return !results.every(Boolean);
}
